/* ---- Spoofing status list (map page detail accordion) ----
 *
 * Phase 4: 8 検知器 + Mock Location を表示。
 *
 * 表示要素:
 *  - バー / % : 各検知器の confidence (= 異常スコア、0-1)。
 *               0% = 完全に正常、100% = 完全に異常検知。
 *  - ピル (正常/注意): SDK の各検知器 isInconsistent (boolean) に直結。
 *  - グレーアウト: 現在のコンテキスト (屋内外 × 動静) で動的重み ≤ 0.05 の検知器
 *                  「今の状況下では事実上重視されない」ことを視覚化 (内部判定のみ、
 *                  UI に重み数値は表示しない)。
 *  - 「常時」バッジ: Mock Location は context 非依存・OS API 直接判定のため、
 *                   weight 概念が無いことを示す別表示。
 *  - 「未対応」表示 (Phase 4 P3-iOS、ADR-0007):
 *                   `availability[key] === false` の検知器は専用クラスで
 *                   グレーアウト + 「未対応」ラベル。iOS 側で実測されない検知器を
 *                   「異常 0%」と誤認させないための platform-aware 表示。
 */

import type { SpoofingStatus } from '../types';
import { getDetectorDisplayState } from './SpoofingAlert';

type DetectorKey =
  | 'doppler'
  | 'imu'
  | 'signal_quality'
  | 'time'
  | 'signal_structure'
  | 'pressure_altitude'
  | 'doppler_raim'
  | 'qzss_authentication'
  | 'mock_location';

interface AlgoDef {
  /** SpoofingStatus 上のステータスキー */
  key: DetectorKey;
  /** UI 表示名 */
  name: string;
  /** weights / scores map のキー (API 名統一) */
  weightKey: string;
}

const ALGOS: AlgoDef[] = [
  { key: 'doppler', name: 'Doppler Consistency', weightKey: 'doppler' },
  { key: 'imu', name: 'IMU Consistency', weightKey: 'imu' },
  { key: 'signal_quality', name: 'Signal Quality', weightKey: 'signal_quality' },
  { key: 'time', name: 'Time Consistency', weightKey: 'time' },
  { key: 'signal_structure', name: 'Signal Structure', weightKey: 'signal_structure' },
  { key: 'pressure_altitude', name: 'Pressure Altitude', weightKey: 'pressure_altitude' },
  { key: 'doppler_raim', name: 'Doppler RAIM', weightKey: 'doppler_raim' },
  { key: 'qzss_authentication', name: 'QZSS Authentication', weightKey: 'qzss_authentication' },
];

const CONTEXT_LABEL: Record<string, string> = {
  OUTDOOR_DYNAMIC: '屋外 × 移動',
  OUTDOOR_STATIC: '屋外 × 静止',
  INDOOR_DYNAMIC: '屋内 × 移動',
  INDOOR_STATIC: '屋内 × 静止',
};

const ENV_ACC_LABEL: Record<string, string> = {
  HIGH: '高',
  MEDIUM: '中',
  LOW: '低',
};

/** 動的重み ≤ この値の検知器をグレーアウト ("現在のコンテキストでは重視しない") */
const GRAYOUT_WEIGHT_THRESHOLD = 0.05;

export function renderSpoofingStatusList(status: SpoofingStatus): HTMLElement {
  const el = document.createElement('div');
  el.className = 'spl';

  const context = status.environment_context;
  const accuracy = status.environment_accuracy;
  const weights = status.weights || {};
  const scores = status.scores || {};

  let inner = '';

  // コンテキストバッジ (現在の屋内外 × 動静)
  if (context && CONTEXT_LABEL[context]) {
    const accText = accuracy && ENV_ACC_LABEL[accuracy] ? `信頼度 ${ENV_ACC_LABEL[accuracy]}` : '';
    inner += `
      <div class="spl-ctx">
        <span class="spl-ctx-l">${CONTEXT_LABEL[context]}</span>
        ${accText ? `<span class="spl-ctx-a">（${accText}）</span>` : ''}
      </div>
    `;
  }

  // 8 検知器（バー = 異常スコア、グレーアウト = 動的重み判定）
  ALGOS.forEach((algo) => {
    const display = getDetectorDisplayState(status, algo.key);

    // 未対応 (iOS API 制約由来) は行ごと非表示にする
    if (display === 'unsupported') return;

    const stClass = display === 'warning' || display === 'critical' ? 'warning' : 'ok';
    const stLabel = display === 'warning' || display === 'critical' ? '注意' : '正常';

    // バー / % は confidence (異常スコア)
    const score = scores[algo.weightKey] ?? 0;
    const scoreClamped = Math.max(0, Math.min(1, score));
    const scorePct = Math.round(scoreClamped * 100);

    // グレーアウトは動的重み判定のみ (UI に weight 値は出さない)
    const weight = weights[algo.weightKey] ?? 0;
    const muted = weight <= GRAYOUT_WEIGHT_THRESHOLD;

    inner += `
      <div class="spl-row${muted ? ' muted' : ''}" data-detector="${algo.key}">
        <span class="spl-name">${algo.name}</span>
        <div class="spl-bar"><div class="spl-bar-fill ${stClass}" style="width:${scorePct}%"></div></div>
        <span class="spl-pct">${scorePct}%</span>
        <div class="dp ${stClass}">
          <div class="dd ${stClass}"></div>
          <span>${stLabel}</span>
        </div>
      </div>
    `;
  });

  // Mock Location（コンテキスト非依存・常時有効、weight/score 概念なし）
  // 未対応の場合は行ごと非表示。iOS でも mock_location は通常 availability=true で出る
  const mockDisplay = getDetectorDisplayState(status, 'mock_location');
  if (mockDisplay !== 'unsupported') {
    const mockClass = mockDisplay === 'warning' || mockDisplay === 'critical' ? 'warning' : 'ok';
    const mockLabel = mockDisplay === 'warning' || mockDisplay === 'critical' ? '注意' : '正常';
    inner += `
      <div class="spl-row" data-detector="mock_location">
        <span class="spl-name">Mock Location</span>
        <span class="spl-always">常時</span>
        <div class="dp ${mockClass}">
          <div class="dd ${mockClass}"></div>
          <span>${mockLabel}</span>
        </div>
      </div>
    `;
  }

  el.innerHTML = inner;
  return el;
}
